<!--
  - Copyright (c) 2020. Minerva Networks.
  - All rights reserved.
  -->

<template>
  <div v-if="home" class="Home" :class="{ blur: me.onboarding && !isWidget }">
    <Redeem v-if="displayRedeem" />
    <template v-if="!displayRedeem">
      <div class="blackBkg">
        <div class="mainWrapper">
          <navBar v-show="!$store.state.singleV"></navBar>
        </div>
      </div>
      <div v-if="home.embedded_video" class="mainWrapper">
        <div class="LiveVideo">
          <div class="LiveVideo__video">
            <div class="playerWrapper livePlayer" :style="homeCover">
              <template v-show="livePlayerDisplay">
                <template
                  v-if="home.embedded_video.video_id === 'c51abf78-784f-11ea-a0c9-06daa3423294'">
                  <a href="https://grabadorus2.tok.tv/rtc/elearning/student.html">
                    <img style="max-width: 100%;"
                         :src="home.embedded_video.cover.replace('{$width}', '1080')" alt="">
                  </a>
                </template>
                <template v-else>
                  <mute :muted="muted" @toggleMute="toggleMute()" />
                  <live
                    v-if="home.embedded_video.live_badge_shown"
                    :position="home.embedded_video.live_badge_position"
                  />
                  <PlayPause
                    v-if="livePlayerDisplay && livePlayerInstance && home.embedded_video.video_type === 'live'"
                    :player-state="livePlayerState"
                    :player-instance="livePlayerInstance"
                  />
                  <Fullscreen v-if="livePlayerDisplay"></Fullscreen>
                  <div class="playerOffsetter">
                    <video-player
                      class="video-player-box liveInstance"
                      ref="liveVideoPlayer"
                      :options="livePlayerOptions"
                      @ready="playerReadied($event)"
                      @play="onLivePlayerPlay($event)"
                      @pause="onLivePlayerPause($event)"
                      @ended="onLivePlayerEnded($event)"
                      @timeupdate="onLiveTimeUpdate($event)"
                    >
                    </video-player>
                  </div>
                </template>
              </template>
            </div>
          </div>
          <div class="LiveVideo__info infoColumn">
            <div class="infoColumn__current">
              <div class="infoColumn__current__text">
                <div class="infoColumn__current__text__title">{{ home.embedded_video.title }}</div>
                <div class="infoColumn__current__text__body"
                     v-html="nl2nr(home.embedded_video.description)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="Fader">
        <div class="Fader__before">
          <div class="Fader__before__end"></div>
          <div class="Fader__before__init"></div>
        </div>
        <div class="Fader__center">
          <Alive v-show="!$store.state.singleV" />
        </div>
        <div class="Fader__after">
          <div class="Fader__after__init"></div>
          <div class="Fader__after__end"></div>
        </div>
      </div>
      <div class="Page__footer">
        <span>© 2020-2021 by A-LIVE</span>
        <span class="hfs">&nbsp;-&nbsp;</span>
        <span><a href="http://www.tok.tv/alive/privacypolicy_en.html">{{ $t('privacy_policy') }}</a></span>
        <span class="hfs">&nbsp;-&nbsp;</span>
        <span><a href="http://www.tok.tv/alive/termsofuse_en.html">{{ $t('terms_of_use') }}</a></span>
        <span class="hfs">&nbsp;-&nbsp;</span>
        <span><router-link to="/faq">{{ $t('faq') }}</router-link></span>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-redeclare,global-require,import/no-dynamic-require */
/* eslint max-len: ["error", { "code": 150 }] */

import {
  mapState, mapMutations, mapActions, mapGetters,
} from 'vuex';
import isRetina from 'is-retina';
import MobileDetect from 'mobile-detect';
import get from 'lodash.get';
import nl2nr from 'nl2br';
import navBar from './NavBar.vue';
import mute from './glyphs/Mute.vue';
import live from './glyphs/Live.vue';
import PlayPause from './glyphs/PlayPause.vue';
import Fullscreen from './glyphs/Fullscreen.vue';
import Alive from './Alive.vue';
import Redeem from './Redeem.vue';

import { manageExceptions } from '../utils/exception';

export default {
  name: 'Home',
  props: {
    hid: {
      type: String,
      required: false,
    },
    vid: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      nl2nr,
      fullscreen: false,
      hls: null,
      playerMenu: true,
      muted: true,
      homeCover: false,
      playerSize: {
        height: 0,
        width: 0,
      },
      retina: isRetina(),
    };
  },
  methods: {
    ...mapActions([
      'enqueueNestedCovers',
      'enqueueImage',
      'processEnqueuedImages',
      'setHomeData',
      'getMe',
      'getPlans',
      'getHome',
      'playVideoById',
    ]),
    ...mapMutations([
      'onPlayerEnded',
      'onPlayerPause',
      'playerStateChanged',
      'onPlayerPlay',
      'appReady',
      'setMd',
      'removeFromQueue',
      'setLivePlayerInstance',
      'onLivePlayerEnded',
      'onLivePlayerPause',
      'onLivePlayerPlay',
      'onLiveTimeUpdate',
      'onLiveSeeked',
      'livePlayerStateChanged',
      'displayLivePlayer',
      'setLoginInfo',
      'redirectToIP',
      'setNavTitle',
      'resetAnimation',
    ]),
    login() {
      this.redirectToIP({
        router: this.$router,
        registered: true,
        ls: this.$ls,
      });
    },
    getToken(ls) {
      const status = ls.get('storageAccessData');
      if (status && status.token) {
        return status.token;
      }
      this.redirectToIP({
        router: this.$router,
        registered: false,
      });
      return true;
    },
    processHomeImages() {
      if (get(this, 'home.embedded_video', false)) {
        if (this.getLPI !== null) {
          const liveVideoStream = get(this, 'home.embedded_video.stream_hls', false);
          if (liveVideoStream) {
            this.playLiveVideo({
              url: liveVideoStream,
              muted: true,
            });
          }
        }
      }
      this.appReady(true);
      window.ga('tok.send', 'screenview', { screenName: 'Main', appName: 'web' });
    },
    toggleMute() {
      const state = this.getLPI.muted();
      this.getLPI.muted(!state);
      this.muted = !state;
      // const videoId = get(this, 'home.embedded_video.video_id', '');
      /*
      if (this.muted) {
        window.ga('tok.send', 'event', 'Main Embedded Video', 'Video Muted', videoId);
      } else {
        window.ga('tok.send', 'event', 'Main Embedded Video', 'Video Unmuted', videoId);
      }
     */
    },
    playerReadied(player) {
      this.displayLivePlayer(true);
      try {
        const liveCover = get(this, 'home.embedded_video.cover', false);
        if (liveCover) {
          const scaledLiveCover = this.scaleImage(liveCover);
          player.poster(scaledLiveCover);
        }
      } catch (e) {
        this.$log.error(e);
      }
      this.setLivePlayerInstance(player);
      player.muted(true);
      this.hls = player.tech({ IWillNotUseThisInPlugins: true }).hls;
    },
    playLiveVideo(config) {
      this.livePlayerOptions.sources[0].src = config.url;
      this.livePlayerOptions.poster = config.cover;
    },
  },
  beforeMount() {
    this.setNavTitle('');
    this.$ls.remove('widgetMode');
  },
  mounted() {
    const that = this;
    if (this.hid) {
      this.$ls.set('singleH', this.hid, 5 * 1000);
    }
    const hid = this.$ls.get('singleH', false);
    this.$store.state.singleH = hid;

    if (this.vid) {
      this.$ls.set('singleV', this.vid, 60 * 60 * 1000);
    } else {
      this.$ls.remove('singleV');
    }
    // this.$ls.set('singleV', this.conf[this.env].concert, 60 * 60 * 1000);
    const lsId = this.$ls.get('singleV', false);
    const theVideoID = get(this.concertMap, lsId, lsId);
    this.$store.state.singleV = theVideoID;
    if (!/login\?status=/.test(window.location.href)) {
      let status = this.$cookies.get(`${window.TOK_NS}storageAccessData`);
      if (!status || status == null) {
        status = this.$ls.get('storageAccessData', false);
      }
      if (status && status != null) {
        this.setLoginInfo(status);
        if (!this.me) {
          this.getMe()
            .then(() => {
              if (get(that, 'me.active_offers', false)) {
                window.ga('tok.set', {
                  dimension7: get(that, 'me.active_offers9', [])
                    .join(','),
                });
              }
              that.getPlans();
              if (this.me.registered) {
                // debugger;
                window.start(this.getToken, this.reactionStripeConfig);
                window.initBar();
              }
            })
            .catch((error) => {
              manageExceptions(error, that);
              window.app.$log.error(error);
            });
        } else if (this.me.registered) {
          window.start(this.getToken, this.reactionStripeConfig);
          window.initBar();
        }
      } else {
        return that.redirectToIP({
          router: that.$router,
          registered: false,
          ls: that.$ls,
        });
      }
    }

    if (this.$route.name === 'clean') {
      this.setHomeData(false);
      this.$ls.remove('storageHomeData');
      this.$router.push('/');
    }

    if (this.$store.state.singleH) {
      this.loadTheHighlight();
    }
    this.resetAnimation();
    const md = new MobileDetect(window.navigator.userAgent);
    this.setMd(md);
    if (!this.home) {
      const lsHomeData = this.$ls.get('storageHomeData', false);
      if (lsHomeData) {
        this.setHomeData(lsHomeData)
          .then(() => {
            that.processHomeImages();
          })
          .catch((error) => {
            that.$log.error(error);
          });
      } else {
        this.getHome(that.$ls)
          .then(() => {
            that.processHomeImages();
          })
          .catch((error) => {
            manageExceptions(error, that);
            that.$log.info('Unable to retrive Home data');
          });
      }
    } else {
      that.processHomeImages();
    }
    const na = this.$ls.get('nextAction', false);
    const np = this.$ls.get('nextPayload', false);
    if (na !== false && np !== false) {
      if (get(this, 'me.registered', false)) {
        this[na]({
          ...np,
          router: this.$router,
        });
      }
    }
    return true;
  },
  components: {
    // categoryCarousel,
    // categories,
    navBar,
    mute,
    live,
    PlayPause,
    Fullscreen,
    Alive,
    Redeem,
  },
  computed: {
    ...mapActions(['loadTheHighlight', 'loadTheVideo']),
    ...mapGetters(['scaleImage', 'getLPI', 'getPI', 'getToken']),
    ...mapState([
      'reactionStripeStatus',
      'reactionStripeConfig',
      'baseUrl',
      'player',
      'highlights',
      'playlist',
      'plays',
      'players',
      'categories',
      'imageQueue',
      'loading',
      'api',
      'home',
      'livePlayerDisplay',
      'livePlayerOptions',
      'livePlayerInstance',
      'livePlayerState',
      'loginInfo',
      'me',
      'plans',
      'locale',
      'appName',
      'nextAction',
      'nextPayload',
      'displayRedeem',
      'redeem',
      'md',
      'concertMap',
      'conf',
      'env',
    ]),
    mClass() {
      return this.md.mobile() ? 'm-' : '';
    },
    mobile() {
      return this.md.mobile() ? 'mobile' : '';
    },
  },
};
</script>
<style lang="scss">

.LiveVideo {
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-wrap: wrap;

  &__video {
    flex: 0 0 100%;
    @media (min-width: 768px) {
      flex: 0 0 60%;
    }
  }

  &__info {
    display: none;
    @media (min-width: 768px) {
      display: flex;
      flex: 0 0 40%;
    }
  }
}

.Home {
  -webkit-transition: -webkit-filter 500ms linear;
  filter: blur(0);
  &.blur {
    filter: blur(5px);
  }
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.Fader {
  overflow: hidden;
  display: flex;
  justify-content: center;
  flex: auto;
  &__before {
    flex: auto;
    position: relative;
    z-index: 2;
    display: flex;
    &__end{
      background: rgb(20,20,20);
      flex: auto;
    }
    &__init {
      flex: 0 0 50px;
      @media (min-width: 1300px) {
        background: rgb(20,20,20);
        background: -moz-linear-gradient(90deg, rgba(20,20,20,1) 50%, rgba(20,20,20,0) 100%);
        background: -webkit-linear-gradient(90deg, rgba(20,20,20,1) 50%, rgba(20,20,20,0) 100%);
        background: linear-gradient(90deg, rgba(20,20,20,1) 50%, rgba(20,20,20,0) 100%);
        filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#141414",endColorstr="#141414",GradientType=1);
      }
    }

  }
  &__center {
    flex: 0 1 1200px;
  }
  &__after {
    position: relative;
    z-index: 2;
    flex: auto;
    display: flex;
    &__init {
      flex: 0 0 50px;
      background: rgb(20,20,20);
      background: -moz-linear-gradient(90deg, rgba(20,20,20,0) 0%, rgba(20,20,20,1) 50%);
      background: -webkit-linear-gradient(90deg, rgba(20,20,20,0) 0%, rgba(20,20,20,1) 50%);
      background: linear-gradient(90deg, rgba(20,20,20,0) 0%, rgba(20,20,20,1) 50%);
      filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#141414",endColorstr="#141414",GradientType=1);
    }
    &__end {
      background: rgb(20,20,20);
      flex: auto;
    }
  }
}
.Page__footer {
  background-color: #000000;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #757575;
  a {
    color: #757575;
  }
  flex-direction: column;
  padding: 20px 0;
  @media (min-width: 500px) {
    padding: 0;
    height: 70px;
    flex-direction: row;
  }
}
.hfs{
  opacity: 0;
  @media (min-width: 500px) {
    opacity: 1;
  }
}
</style>
